import { useState } from 'react';
import { enqueueSnackbar } from 'notistack';

import { useTranslate } from 'src/locales';
import { IntegrationStatus, IntegrationCategory, CommunicationEntityRef } from 'src/services/integration/integrations.types';
import {
  useGetConnectedIntegrationsQuery,
  useInitiateCommunicationCallMutation,
} from 'src/services/integration/integrations.service';

import SmsComposeModal from './sms-compose-modal';

// ----------------------------------------------------------------------

/**
 * Build the best dial string from a record's phone + optional country code.
 * The backend finalises to E.164 (applying the org/sender region for a bare
 * national number), so this only needs to avoid mangling existing data:
 *  - already-international (+…) passes through
 *  - a separate country code is prefixed, stripping a national trunk "0"
 *  - otherwise the bare number is sent and the backend applies the region
 */
export function buildDialNumber(phone?: string | null, countryCode?: string | null): string | undefined {
  const raw = (phone ?? '').trim();
  if (!raw) return undefined;
  if (raw.startsWith('+')) return raw;
  const cc = (countryCode ?? '').trim();
  if (cc) {
    const prefix = cc.startsWith('+') ? cc : `+${cc.replace(/^0+/, '')}`;
    return `${prefix}${raw.replace(/^0+/, '')}`;
  }
  return raw;
}

/**
 * Shared click-to-call / SMS behaviour. Returns whether comms is usable for the
 * given number, a `call()` action, an `openSms()` trigger, and the SMS modal
 * node — render `smsModal` somewhere persistent (e.g. at the row root, NOT
 * inside a popover that closes) so it survives the menu closing.
 */
export function useCommunicationActions(phoneNumber?: string | null, name?: string, entity?: CommunicationEntityRef) {
  const { t } = useTranslate();
  const [smsOpen, setSmsOpen] = useState(false);

  const { data: connected } = useGetConnectedIntegrationsQuery();
  const [initiateCall, { isLoading: calling }] = useInitiateCommunicationCallMutation();

  const commsActive = !!phoneNumber && (connected?.results ?? []).some(
    (i) => i.category === IntegrationCategory.COMMUNICATIONS && i.status === IntegrationStatus.ACTIVE
  );

  const call = async () => {
    if (!phoneNumber) return;
    try {
      await initiateCall({ to_number: phoneNumber, ...entity }).unwrap();
      enqueueSnackbar(t('integrations.communications.actions.call_success'), { variant: 'success' });
    } catch (e: any) {
      enqueueSnackbar(e?.data?.message || t('integrations.communications.actions.error'), { variant: 'error' });
    }
  };

  const openSms = () => setSmsOpen(true);

  const smsModal = phoneNumber ? (
    <SmsComposeModal open={smsOpen} toNumber={phoneNumber} name={name} entity={entity} onClose={() => setSmsOpen(false)} />
  ) : null;

  return { commsActive, call, calling, openSms, smsModal };
}
